/**
 * How this strategy's definition is written (LAR-1).
 *
 * **Two modes, and the missing third is the interesting one.** Trade's page offered `api`,
 * `dsl` and `portfolio`. `api` is not restored here, and not because it was forgotten — decision
 * LAR-D1: an `api` strategy stores no definition, so Lab's backtest skips it, the weight cache
 * never fills, shortlisting finds no hash to shortlist and export refuses it by name
 * (`unsupported_source_mode`). Creating one in a research workspace produces a row that can be
 * listed and nothing else.
 *
 * The old page defaulted to `api`. This one has no such mode and defaults to DSL, which is what
 * almost every strategy here actually is.
 */
import type { LabSourceMode } from '../labStrategyApi';

const MODES: { value: LabSourceMode; label: string; hint: string }[] = [
  {
    value: 'dsl',
    label: 'DSL',
    hint: 'Composer DSL code. Weights are computed here, from the definition you write.',
  },
  {
    value: 'portfolio',
    label: 'Portfolio',
    hint: 'A vrk-portfolio/v1 document allocating across sleeves you have already written.',
  },
];

interface Props {
  value: LabSourceMode;
  disabled?: boolean;
  onChange: (mode: LabSourceMode) => void;
}

export function StrategySourceSelector({ value, disabled, onChange }: Props) {
  return (
    <fieldset>
      <legend className="text-sm font-medium text-slate-700">Source</legend>
      <div className="mt-1.5 flex flex-wrap gap-2">
        {MODES.map((mode) => (
          <label
            key={mode.value}
            className={`cursor-pointer rounded-lg border px-3 py-1.5 text-sm font-medium ${
              value === mode.value
                ? 'border-emerald-700 bg-emerald-50 text-emerald-900'
                : 'border-slate-300 text-slate-700 hover:bg-slate-50'
            } ${disabled ? 'pointer-events-none opacity-60' : ''}`}
          >
            <input
              type="radio"
              name="strategy-source-mode"
              value={mode.value}
              checked={value === mode.value}
              disabled={disabled}
              onChange={() => onChange(mode.value)}
              className="sr-only"
            />
            {mode.label}
          </label>
        ))}
      </div>
      <p className="mt-1.5 text-xs text-slate-500">
        {MODES.find((mode) => mode.value === value)?.hint}
      </p>
    </fieldset>
  );
}
